/**
 * ⭐⭐ PROPERTY TAXES DO NOT GROW LIKE INSURANCE, AND IN CALIFORNIA THEY LEGALLY CANNOT.
 *
 * 🚨 EARNED ON STAGECOACH PLAZA, 2026-08-30. The OM's own CBRE cash flow grows CAM, utilities,
 * insurance and the management fee at 3% and **real estate taxes at 2% - the Proposition 13 cap on
 * annual assessed-value growth.** Taxes were $64,375 of a $137,415 schedule, nearly half of it,
 * and the app grew the whole schedule at one rate:
 *
 *     year   CBRE total   split 2%/3%   flat 3%    overshoot
 *       Y1      137,415       137,415    137,415           0
 *       Y3      144,443       144,464    145,784       1,341
 *       Y5      151,846       151,889    154,662       2,816
 *
 * ⭐ **A 2%/3% split reproduces the broker's schedule within $50 EVERY YEAR.** One flat rate
 * overshoots by $2,816 by year 5 and keeps compounding - and it compounds in the direction that
 * UNDERSTATES NOI, so it is not even the flattering error, just the wrong one.
 *
 * ⛔ THE RULEBOOK ALREADY HALF-KNEW THIS. Joseph's Prop 13 rule reassesses taxes to the purchase
 * price on acquisition - the app applies it - and then grew the reassessed figure like any other
 * line. **The same statute that resets the basis also caps its growth**, and only half of it was
 * modelled. This is a California-only app.
 *
 * ⛔ ABSENT MEANS PREVIOUS BEHAVIOUR. With no tax rate supplied, or with taxes of zero, this
 * reduces exactly to `total * (1 + g)^n`, so no saved analysis moves. Pinned as the first test.
 */
export interface GrownExpensesInput {
  /** Year-1 operating expenses, ALL lines including taxes. */
  totalExpenses: number;
  /** The property-tax portion of that total. 0 when unknown or none. */
  propertyTaxes: number;
  /** Annual growth for everything except taxes, as a FRACTION - 0.03, not 3. */
  expenseGrowthRate: number;
  /**
   * Annual growth for the tax line, as a FRACTION. `null`/`undefined` means "same as the rest",
   * which is the behaviour every existing analysis was saved under.
   */
  taxGrowthRate?: number | null;
  /** Whole years of compounding. 0 for year 1. */
  periods: number;
}

export const grownExpenses = (input: GrownExpensesInput): number => {
  const { totalExpenses, propertyTaxes, expenseGrowthRate, taxGrowthRate, periods } = input;
  const other = totalExpenses - propertyTaxes;

  // ⛔ No separate rate, no split. Identical arithmetic to before this file existed.
  if (typeof taxGrowthRate !== 'number' || !isFinite(taxGrowthRate)) {
    return totalExpenses * Math.pow(1 + expenseGrowthRate, periods);
  }
  // ⛔ A tax figure larger than the total, or negative, is not splittable - refuse rather than
  // produce a negative "other" line that would silently inflate NOI.
  if (propertyTaxes <= 0 || propertyTaxes > totalExpenses) {
    return totalExpenses * Math.pow(1 + expenseGrowthRate, periods);
  }
  return other * Math.pow(1 + expenseGrowthRate, periods)
    + propertyTaxes * Math.pow(1 + taxGrowthRate, periods);
};

/** The Proposition 13 cap on annual growth in assessed value. Seeded for new California analyses. */
export const PROP_13_ANNUAL_CAP_PERCENT = 2;
